/**
 * Help Documentation Page - Comprehensive guide for using CamPassed
 */

import { useState } from 'react';
import {
  Book,
  ChevronRight,
  Command,
  Calendar,
  Users,
  QrCode,
  DollarSign,
  Building2,
  BarChart3,
  Settings,
  Shield,
  Megaphone,
  Search,
  ExternalLink,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';

// Help Section Component
const HelpSection = ({ id, title, icon: Icon, children }) => (
  <section id={id} className="scroll-mt-8">
    <div className="flex items-center gap-3 mb-4">
      <div className="p-2 bg-electric-ice-blue/10 rounded-lg">
        <Icon className="w-5 h-5 text-electric-ice-blue" />
      </div>
      <h2 className="text-xl font-semibold text-cloud-white">{title}</h2>
    </div>
    <div className="prose prose-invert max-w-none text-soft-silver">
      {children}
    </div>
  </section>
);

// Help Article Component
const HelpArticle = ({ title, children }) => (
  <div className="mb-6">
    <h3 className="text-lg font-medium text-cloud-white mb-2">{title}</h3>
    <div className="text-soft-silver space-y-2">{children}</div>
  </div>
);

// Navigation Item
const NavItem = ({ href, icon: Icon, label, active }) => (
  <a
    href={href}
    className={cn(
      "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors",
      active
        ? "bg-electric-ice-blue/10 text-electric-ice-blue"
        : "text-soft-silver hover:text-cloud-white hover:bg-white/[0.05]"
    )}
  >
    <Icon className="w-4 h-4" />
    {label}
  </a>
);

export const Help = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState('getting-started');

  const sections = [
    { id: 'getting-started', label: 'Getting Started', icon: Book },
    { id: 'command-center', label: 'Command Center', icon: Command },
    { id: 'events', label: 'Events', icon: Calendar },
    { id: 'guests', label: 'Guest Management', icon: Users },
    { id: 'scanner', label: 'Door Scanner', icon: QrCode },
    { id: 'members', label: 'Members', icon: Users },
    { id: 'sponsors', label: 'Sponsors', icon: Building2 },
    { id: 'campaigns', label: 'Campaigns & Ads', icon: Megaphone },
    { id: 'monetization', label: 'Monetization', icon: DollarSign },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'security', label: 'Security', icon: Shield },
  ];

  return (
    <div className="flex gap-8" data-testid="help-page">
      {/* Sidebar Navigation */}
      <aside className="w-64 shrink-0 sticky top-4 h-fit">
        <div className="bg-graphite/50 rounded-2xl border border-white/[0.06] p-4">
          <h3 className="text-sm font-medium text-cloud-white mb-4">Documentation</h3>
          <div className="space-y-1">
            {sections.map((section) => (
              <NavItem
                key={section.id}
                href={`#${section.id}`}
                icon={section.icon}
                label={section.label}
                active={activeSection === section.id}
              />
            ))}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 space-y-12">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-cloud-white">Help Center</h1>
          <p className="text-soft-silver mt-2">Learn how to use CamPassed to manage your events and grow your community.</p>
          
          {/* Search */}
          <div className="relative mt-6 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-text" />
            <Input
              type="text"
              placeholder="Search documentation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-obsidian border-white/[0.08] text-cloud-white"
            />
          </div>
        </div>

        {/* Getting Started */}
        <HelpSection id="getting-started" title="Getting Started" icon={Book}>
          <HelpArticle title="Welcome to CamPassed">
            <p>CamPassed is a premium SaaS platform designed for campus event operations and social discovery. Whether you're managing a fraternity, sorority, club, or event promotion company, CamPassed provides the tools you need to run successful events.</p>
          </HelpArticle>
          
          <HelpArticle title="Quick Start Guide">
            <ol className="list-decimal list-inside space-y-2">
              <li><strong>Set up your organization</strong> - Complete your organization profile in Settings</li>
              <li><strong>Add team members</strong> - Invite members with appropriate roles (Admin, Event Manager, Member)</li>
              <li><strong>Create your first event</strong> - Use the Events page to create and publish events</li>
              <li><strong>Manage guests</strong> - Add guests manually or share invite links</li>
              <li><strong>Use the scanner</strong> - Check in guests at the door with QR scanning</li>
            </ol>
          </HelpArticle>

          <HelpArticle title="User Roles">
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Owner</strong> - Full access to all features including billing</li>
              <li><strong>Admin</strong> - Manage events, members, and settings</li>
              <li><strong>Event Manager</strong> - Create/edit events, manage guests</li>
              <li><strong>Member</strong> - View events, check in guests</li>
            </ul>
          </HelpArticle>
        </HelpSection>

        {/* Command Center */}
        <HelpSection id="command-center" title="Command Center" icon={Command}>
          <HelpArticle title="Overview">
            <p>The Command Center is your mission control for event operations. It provides a real-time view of all activity across your organization.</p>
          </HelpArticle>
          
          <HelpArticle title="Features">
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Event Radar</strong> - Visual display of upcoming events with countdown timers</li>
              <li><strong>Live Stats</strong> - Real-time metrics for check-ins, attendance, and revenue</li>
              <li><strong>Operations Feed</strong> - Live activity stream showing guest arrivals and VIP alerts</li>
              <li><strong>Scanner Telemetry</strong> - Monitor door scanner activity across events</li>
              <li><strong>Sponsor Revenue</strong> - Track sponsor campaign performance</li>
            </ul>
          </HelpArticle>
        </HelpSection>

        {/* Events */}
        <HelpSection id="events" title="Events" icon={Calendar}>
          <HelpArticle title="Creating Events">
            <p>To create a new event:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Go to the Events page</li>
              <li>Click "Create Event"</li>
              <li>Fill in event details (name, date, time, location, capacity)</li>
              <li>Set visibility (Public, Private, Members Only)</li>
              <li>Click "Create" to save</li>
            </ol>
          </HelpArticle>

          <HelpArticle title="Event Types">
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Party</strong> - Social gatherings and celebrations</li>
              <li><strong>Formal</strong> - Formal events like galas and banquets</li>
              <li><strong>Mixer</strong> - Networking and social mixers</li>
              <li><strong>Social</strong> - General social events</li>
              <li><strong>Concert</strong> - Music and performance events</li>
            </ul>
          </HelpArticle>

          <HelpArticle title="Managing Events">
            <p>From the event detail page, you can:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Edit event details</li>
              <li>View and manage the guest list</li>
              <li>Generate QR codes for guests</li>
              <li>Monitor check-in progress</li>
              <li>View event analytics</li>
            </ul>
          </HelpArticle>
        </HelpSection>

        {/* Guests */}
        <HelpSection id="guests" title="Guest Management" icon={Users}>
          <HelpArticle title="Adding Guests">
            <p>Guests can be added in several ways:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Manual entry</strong> - Add guests one by one with name and email</li>
              <li><strong>CSV import</strong> - Bulk import from spreadsheet</li>
              <li><strong>Invite links</strong> - Share a link for self-registration</li>
              <li><strong>Plus-ones</strong> - Allow guests to bring additional people</li>
            </ul>
          </HelpArticle>

          <HelpArticle title="Guest Statuses">
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Pending</strong> - Awaiting approval</li>
              <li><strong>Approved</strong> - Guest can attend the event</li>
              <li><strong>Denied</strong> - Guest request declined</li>
              <li><strong>Checked In</strong> - Guest has arrived at the event</li>
            </ul>
          </HelpArticle>

          <HelpArticle title="VIP Guests">
            <p>VIP guests receive special treatment at events. Mark guests as VIP to:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Trigger alerts when they arrive</li>
              <li>Track VIP attendance separately</li>
              <li>Provide priority entry</li>
            </ul>
          </HelpArticle>
        </HelpSection>

        {/* Scanner */}
        <HelpSection id="scanner" title="Door Scanner" icon={QrCode}>
          <HelpArticle title="Using the Scanner">
            <p>The door scanner allows you to check in guests using their QR code passes:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Navigate to the Scanner page</li>
              <li>Select the active event</li>
              <li>Point your camera at the guest's QR code</li>
              <li>The system will automatically validate and check in the guest</li>
            </ol>
          </HelpArticle>

          <HelpArticle title="Scanner Status Indicators">
            <ul className="list-disc list-inside space-y-1">
              <li><strong className="text-cool-mint">Green</strong> - Valid pass, guest checked in</li>
              <li><strong className="text-soft-amber">Yellow</strong> - Already checked in (duplicate scan)</li>
              <li><strong className="text-signal-red">Red</strong> - Invalid or expired pass</li>
            </ul>
          </HelpArticle>

          <HelpArticle title="Manual Check-in">
            <p>If a guest doesn't have their QR code, you can manually check them in by searching for their name in the guest list.</p>
          </HelpArticle>
        </HelpSection>

        {/* Members */}
        <HelpSection id="members" title="Members" icon={Users}>
          <HelpArticle title="Inviting Members">
            <p>To add new members to your organization:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Go to the Members page</li>
              <li>Click "Invite Member"</li>
              <li>Enter their email address</li>
              <li>Select their role</li>
              <li>Send the invitation</li>
            </ol>
          </HelpArticle>

          <HelpArticle title="Managing Roles">
            <p>You can change a member's role at any time from their profile. Only Owners can change roles of Admins.</p>
          </HelpArticle>
        </HelpSection>

        {/* Sponsors */}
        <HelpSection id="sponsors" title="Sponsors" icon={Building2}>
          <HelpArticle title="Adding Sponsors">
            <p>Sponsors are businesses or organizations that partner with you for events:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Go to the Sponsors page</li>
              <li>Click "Add Sponsor"</li>
              <li>Enter sponsor details (name, contact info, logo)</li>
              <li>Save the sponsor</li>
            </ol>
          </HelpArticle>

          <HelpArticle title="Sponsor Benefits">
            <ul className="list-disc list-inside space-y-1">
              <li>Track sponsor engagement across events</li>
              <li>Run advertising campaigns for sponsors</li>
              <li>Share sponsor analytics and reports</li>
            </ul>
          </HelpArticle>
        </HelpSection>

        {/* Campaigns */}
        <HelpSection id="campaigns" title="Campaigns & Ads" icon={Megaphone}>
          <HelpArticle title="Creating Campaigns">
            <p>Campaigns allow sponsors to advertise to your audience:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Go to the Campaigns page</li>
              <li>Click "New Campaign"</li>
              <li>Select a sponsor</li>
              <li>Configure campaign type and budget</li>
              <li>Upload creative assets</li>
              <li>Set date range and launch</li>
            </ol>
          </HelpArticle>

          <HelpArticle title="Campaign Types">
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Banner Ads</strong> - Display ads shown in the app</li>
              <li><strong>Discovery Promo</strong> - Featured placement in event discovery</li>
              <li><strong>Sponsored Event</strong> - Event sponsorship with branding</li>
              <li><strong>Push Notification</strong> - Targeted notifications to users</li>
            </ul>
          </HelpArticle>

          <HelpArticle title="Campaign Analytics">
            <p>Track campaign performance with metrics including:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Impressions - Number of times ad was viewed</li>
              <li>Clicks - Number of user interactions</li>
              <li>CTR - Click-through rate</li>
              <li>Spend - Budget consumed</li>
            </ul>
          </HelpArticle>
        </HelpSection>

        {/* Monetization */}
        <HelpSection id="monetization" title="Monetization" icon={DollarSign}>
          <HelpArticle title="Revenue Streams">
            <p>CamPassed supports multiple revenue streams:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Ticket Sales</strong> - Sell tickets to events</li>
              <li><strong>Sponsor Revenue</strong> - Earn from advertising campaigns</li>
              <li><strong>Premium Features</strong> - Upsell premium student features</li>
            </ul>
          </HelpArticle>

          <HelpArticle title="Payouts">
            <p>Configure payout settings to receive your earnings via Stripe integration.</p>
          </HelpArticle>
        </HelpSection>

        {/* Analytics */}
        <HelpSection id="analytics" title="Analytics" icon={BarChart3}>
          <HelpArticle title="Dashboard Metrics">
            <p>Monitor your organization's performance with key metrics:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Event attendance trends</li>
              <li>Guest check-in rates</li>
              <li>Revenue analytics</li>
              <li>Sponsor campaign performance</li>
            </ul>
          </HelpArticle>
        </HelpSection>

        {/* Settings */}
        <HelpSection id="settings" title="Settings" icon={Settings}>
          <HelpArticle title="Organization Settings">
            <p>Configure your organization profile, notification preferences, and integrations from the Settings page.</p>
          </HelpArticle>
        </HelpSection>

        {/* Security */}
        <HelpSection id="security" title="Security" icon={Shield}>
          <HelpArticle title="Best Practices">
            <ul className="list-disc list-inside space-y-1">
              <li>Use strong, unique passwords</li>
              <li>Review member access regularly</li>
              <li>Monitor the audit log for suspicious activity</li>
              <li>Keep your contact information up to date</li>
            </ul>
          </HelpArticle>
        </HelpSection>

        {/* Contact Support */}
        <div className="bg-graphite/50 rounded-2xl border border-white/[0.06] p-6 mt-12">
          <h2 className="text-lg font-semibold text-cloud-white mb-2">Need More Help?</h2>
          <p className="text-soft-silver mb-4">Can't find what you're looking for? Our support team is here to help.</p>
          <a
            href="mailto:support@campusos.com"
            className="inline-flex items-center gap-2 text-electric-ice-blue hover:underline"
          >
            Contact Support
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </main>
    </div>
  );
};

export default Help;
